import { Button } from "@ondit/ui";
import { useState } from "react";
import { canCheckout } from "~/common/billing/paygate";
import { whyDisabled } from "~/core/access/index";
import type { Plan } from "~/core/api/index";
import * as fmt from "~/core/format";
import { pick, useI18n, useT } from "~/core/i18n";

/** Contract plans have no self-service tier ordering. Showing catalogue plans
 * as upgrades would offer an enterprise customer less than their contract. */
/** The one card that carries the filled button, matching the directory. */
const FEATURED = "standard";

const CONTACT = "mailto:hello@ondit.ai?subject=Enterprise";

export function PlanChooser({
	plans,
	current,
	busy,
	minEngines = 0,
	onChoose,
	onDowngrade,
	pending = null,
	admin = true,
	trialUsed = false,
}: {
	plans: Plan[];
	/** The plan the org is on. Absent in the wizard, where there is none yet. */
	current?: string;
	/** The plan key a checkout is open for, or null. */
	busy: string | null;
	/**
	 * Whether this person may change the account's plan. A member sees the
	 * prices and cannot press them: the route refuses, and a button that always
	 * refuses is a bug report. Open in the wizard, where the person is the owner.
	 */
	admin?: boolean;
	/**
	 * How many engines the brand chose to be measured on. A plan covering
	 * fewer is not offered: it would quietly measure the brand on less than
	 * the person just asked for, and the first they would hear of it is a
	 * chart with a column missing.
	 */
	minEngines?: number;
	onChoose: (planKey: string, interval: "month" | "year") => void;
	/** Absent where moving down is not offered: the wizard, a lapsed account. */
	onDowngrade?: (planKey: string) => void;
	/** The downgrade already scheduled, if one is. */
	pending?: string | null;
	/**
	 * Whether the account has taken its trial month. Once it has, the trial
	 * card is not drawn again — the checkout refuses a second one, and a price
	 * nobody may buy is worse on the screen than absent.
	 */
	trialUsed?: boolean;
}) {
	const t = useT();
	const { locale } = useI18n();
	const [yearly, setYearly] = useState(true);

	// The trial stays on screen while it is the plan somebody is on — that card
	// is how they see what they are about to lose — and goes once it is spent.
	const paid = plans.filter(
		(p) => !p.contact_only && p.price_usd_micros > 0 && (!p.trial || !trialUsed || p.key === current),
	);
	const enterprise = plans.find((p) => p.contact_only);

	// -1 for an org on the free row, which puts every paid plan above it. The
	// rows below the current plan are shown only where moving down is offered.
	const at = current ? paid.findIndex((p) => p.key === current) : -1;
	const contracted = !!enterprise && current === enterprise.key;
	const offered = contracted ? [] : onDowngrade && at > 0 ? paid : paid.slice(at < 0 ? 0 : at);
	// The filled button goes to the plan the directory features, and to the
	// cheapest upgrade when that one is behind the customer already. One filled
	// button, always, because two is a screen with no primary action.
	const above = offered.filter((p) => at < 0 || paid.indexOf(p) > at);
	const featured = above.find((p) => p.key === FEATURED)?.key ?? above[0]?.key;

	// Per month either way: a year's total beside a month's looks like a
	// different product, and the question the switch answers — is a year
	// cheaper — is only answered at a glance by two comparable numbers. The
	// twelfth of a year is not rounded to a dollar: a plan at $290 a year is
	// $24.17 a month, and "$24" beside "$29" is a price the bill will not match.
	const price = (p: Plan) => {
		const yearlyMicros = Number(p.price_usd_yearly_micros);
		// A trial has one price because it has one month. The switch above is
		// still there for the plans it means something for.
		const perYear = yearly && !p.trial && yearlyMicros > 0;
		return fmt.usd(locale, perYear ? yearlyMicros / 12 : Number(p.price_usd_micros));
	};
	const forbidden = whyDisabled(admin, t("access.admin"));

	// The numbers first, then the two parts a plan either has or does not.
	// A line per feature the plan carries rather than a tick beside every
	// feature every plan could carry: a card that lists what is missing is a
	// card somebody reads as a list of what they are losing.
	const features = (p: Plan) =>
		[
			p.engines_max === 1 ? t("plan.engineOne") : t("plan.engines", { n: String(p.engines_max) }),
			p.questions_max > 0 ? t("plan.questions", { n: String(p.questions_max) }) : null,
			// Grouped: five thousand credits beside three seats is a card whose
			// numbers are all read at a glance, and `5000` is the one that is not.
			t("plan.credits", { n: fmt.number(locale, p.monthly_credits) }),
			p.pages_per_month > 0 ? t("plan.pages", { n: String(p.pages_per_month) }) : null,
			p.entities_max === 1 ? t("plan.brandOne") : t("plan.brands", { n: String(p.entities_max) }),
			p.seats_max === 1 ? t("plan.seatOne") : t("plan.seats", { n: String(p.seats_max) }),
			t("plan.agent"),
			p.workflows_max === 0 ? t("plan.workflowsUnlimited") : t("plan.workflows", { n: p.workflows_max }),
			t("plan.builtinWorkflows"),
			p.advanced_insights ? t("plan.advanced") : null,
		].filter((line): line is string => line !== null);

	return (
		<div className="plan-picker">
			{/* A fieldset: two exclusive choices with a name, which is what one is for. */}
			{offered.length > 0 ? (
				<fieldset className="pricing-switch">
					<legend className="visually-hidden">{`${t("plan.monthly")} / ${t("plan.yearly")}`}</legend>
					<Button
						variant={yearly ? "ghost" : "active"}
						aria-pressed={!yearly}
						type="button"
						onClick={() => setYearly(false)}
					>
						{t("plan.monthly")}
					</Button>
					<Button
						variant={yearly ? "active" : "ghost"}
						aria-pressed={yearly}
						type="button"
						onClick={() => setYearly(true)}
					>
						{t("plan.yearly")} <span className="pricing-badge">{t("plan.twoMonthsFree")}</span>
					</Button>
				</fieldset>
			) : null}

			<div className={contracted ? "pricing-grid is-contracted" : "pricing-grid"}>
				{offered.map((p) => {
					const mine = p.key === current;
					const lead = p.key === featured;
					const below = at >= 0 && paid.indexOf(p) < at;
					const scheduled = pending === p.key;
					return (
						<article key={p.key} className={`pricing-card ${lead ? "featured" : ""}`}>
							{lead ? <span className="pricing-popular">{t("plan.popular")}</span> : null}
							{scheduled ? <span className="pricing-popular">{t("plan.scheduled")}</span> : null}
							<h2>{pick(locale, [p.name_en, p.name_zh])}</h2>
							<p className="pricing-price">
								<strong className="num">{price(p)}</strong>
								<span className="muted">/{t("plan.perMonth")}</span>
							</p>
							{p.trial ? (
								<p className="pricing-note">{t("plan.trialOnce")}</p>
							) : yearly ? (
								<p className="pricing-note">{t("plan.billedYearly")}</p>
							) : null}
							<p className="pricing-blurb">{t(`plan.blurb.${p.key}`)}</p>
							{/* Offered, not hidden: the cheapest plan is the one somebody
							    came to look at, and what it costs them is a sentence. */}
							{minEngines > p.engines_max ? (
								<p className="pricing-note">
									{t("plan.enginesFewer", { n: String(p.engines_max), m: String(minEngines) })}
								</p>
							) : null}
							<ul className="pricing-features">
								{features(p).map((line) => (
									<li key={line}>{line}</li>
								))}
							</ul>
							{below ? (
								<Button
									variant="default"
									type="button"
									disabled={busy !== null || scheduled || !admin}
									title={forbidden}
									onClick={() => onDowngrade?.(p.key)}
								>
									{scheduled ? t("plan.scheduled") : busy === p.key ? t("login.working") : t("plan.downgrade")}
								</Button>
							) : (
								<Button
									variant={lead ? "default" : "outline"}
									type="button"
									disabled={mine || busy !== null || !canCheckout || !admin}
									title={mine ? undefined : forbidden}
									onClick={() => onChoose(p.key, yearly && !p.trial ? "year" : "month")}
								>
									{mine
										? t("plan.current")
										: busy === p.key
											? t("login.working")
											: current
												? t("plan.upgrade")
												: t("plan.subscribe")}
								</Button>
							)}
						</article>
					);
				})}

				{enterprise ? (
					<article className="pricing-card">
						{contracted ? <span className="pill">{t("plan.current")}</span> : null}
						<h2>{pick(locale, [enterprise.name_en, enterprise.name_zh])}</h2>
						<p className="pricing-price">
							<strong>{t("plan.custom")}</strong>
						</p>
						<p className="pricing-blurb">{t("plan.blurb.enterprise")}</p>
						<ul className="pricing-features">
							<li>{t("plan.enginesAll")}</li>
							<li>{t("plan.brandsMany")}</li>
							<li>{t("plan.questionsCustom")}</li>
							<li>{t("plan.sso")}</li>
							<li>{t("plan.supportDedicated")}</li>
						</ul>
						<Button asChild variant="default">
							<a href={CONTACT}>{t("plan.contact")}</a>
						</Button>
					</article>
				) : null}
			</div>

			{/* Which plans the engine choice ruled out, so the shorter list reads
			    as a consequence of a choice rather than a catalogue with holes. */}
			{minEngines > 0 && offered.some((p) => p.engines_max < minEngines) ? (
				<p className="pricing-note">{t("plan.enginesNarrowed", { n: String(minEngines) })}</p>
			) : null}

			{/* A build with no gateway behind it. Saying so beside the dead
			    buttons is the only honest thing to render: the alternative is a
			    customer clicking a price four times. */}
			{canCheckout ? null : <p className="pricing-note">{t("billing.noCheckout")}</p>}
		</div>
	);
}
